/**
 * Invoice Calculation Module
 * 
 * Centralized, single source of truth for all invoice calculations.
 * All functions are pure, testable, and handle numeric precision properly.
 * 
 * Calculation Order:
 * 1. Line Item Amount = quantity × rate
 * 2. Subtotal = sum of all line item amounts
 * 3. Discounted Subtotal = subtotal - discount
 * 4. Tax Amount = discounted subtotal × (tax rate / 100)
 * 5. Total = discounted subtotal + tax amount
 */

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Default precision for monetary calculations
 * Most currencies use 2 decimal places, JPY uses 0
 */
export const DEFAULT_DECIMAL_PLACES = 2;

/**
 * Currencies that don't use decimal places
 */
export const ZERO_DECIMAL_CURRENCIES = ['JPY'];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Safely parse a value to a number, returning 0 for invalid inputs
 * @param {any} value - The value to parse
 * @returns {number} Parsed number or 0
 */
export function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Round a number to specified decimal places using banker's rounding
 * This prevents floating point precision errors
 * @param {number} value - The value to round
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {number} Rounded value
 */
export function roundToDecimals(value, decimals = DEFAULT_DECIMAL_PLACES) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  
  // Use exponential notation to avoid floating point errors
  const multiplier = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
}

/**
 * Get the number of decimal places for a currency
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {number} Number of decimal places
 */
export function getDecimalPlaces(currencyCode = 'USD') {
  return ZERO_DECIMAL_CURRENCIES.includes(currencyCode) ? 0 : DEFAULT_DECIMAL_PLACES;
}

/**
 * Clamp a value between min and max
 * @param {number} value - Value to clamp
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Clamped value
 */
export function clamp(value, min = 0, max = Infinity) {
  return Math.min(Math.max(toNumber(value), min), max);
}

// ============================================================================
// LINE ITEM CALCULATIONS
// ============================================================================

/**
 * Calculate line item amount (quantity × rate)
 * @param {number} quantity - Item quantity
 * @param {number} rate - Unit price/rate
 * @param {string} currencyCode - Currency code for precision
 * @returns {number} Calculated amount
 */
export function calculateLineItemAmount(quantity, rate, currencyCode = 'USD') {
  const decimals = getDecimalPlaces(currencyCode);
  const qty = clamp(toNumber(quantity), 0);
  const unitRate = clamp(toNumber(rate), 0);
  
  return roundToDecimals(qty * unitRate, decimals);
}

/**
 * Create a properly formatted line item with calculated amount
 * @param {Object} item - Raw line item object
 * @param {string} currencyCode - Currency code for precision
 * @returns {Object} Line item with calculated amount
 */
export function createLineItem(item = {}, currencyCode = 'USD') {
  const quantity = clamp(toNumber(item.quantity), 0);
  const rate = clamp(toNumber(item.rate), 0);
  const amount = calculateLineItemAmount(quantity, rate, currencyCode);
  
  return {
    description: item.description || '',
    quantity,
    rate,
    amount,
  };
}

/**
 * Create a default empty line item
 * @returns {Object} Empty line item
 */
export function createEmptyLineItem() {
  return {
    description: '',
    quantity: 1,
    rate: 0,
    amount: 0,
  };
}

// ============================================================================
// INVOICE TOTAL CALCULATIONS
// ============================================================================

/**
 * Calculate subtotal from line items
 * @param {Array} items - Array of line items with amount property
 * @param {string} currencyCode - Currency code for precision
 * @returns {number} Subtotal
 */
export function calculateSubtotal(items = [], currencyCode = 'USD') {
  const decimals = getDecimalPlaces(currencyCode);
  
  if (!Array.isArray(items) || items.length === 0) {
    return 0;
  }
  
  const sum = items.reduce((acc, item) => {
    return acc + toNumber(item?.amount);
  }, 0);
  
  return roundToDecimals(sum, decimals);
}

/**
 * Calculate tax amount based on discounted subtotal
 * @param {number} subtotal - Invoice subtotal
 * @param {number} discount - Discount amount (not percentage)
 * @param {number} taxRate - Tax rate as percentage (e.g., 10 for 10%)
 * @param {string} currencyCode - Currency code for precision
 * @returns {number} Tax amount
 */
export function calculateTaxAmount(subtotal, discount, taxRate, currencyCode = 'USD') {
  const decimals = getDecimalPlaces(currencyCode);
  const sub = toNumber(subtotal);
  const disc = clamp(toNumber(discount), 0, sub); // Discount can't exceed subtotal
  const rate = clamp(toNumber(taxRate), 0, 100); // Tax rate between 0-100%
  
  const taxableAmount = Math.max(0, sub - disc);
  const taxAmount = taxableAmount * (rate / 100);
  
  return roundToDecimals(taxAmount, decimals);
}

/**
 * Calculate final invoice total
 * @param {number} subtotal - Invoice subtotal
 * @param {number} discount - Discount amount
 * @param {number} taxAmount - Calculated tax amount
 * @param {string} currencyCode - Currency code for precision
 * @returns {number} Final total
 */
export function calculateTotal(subtotal, discount, taxAmount, currencyCode = 'USD') {
  const decimals = getDecimalPlaces(currencyCode);
  const sub = toNumber(subtotal);
  const disc = clamp(toNumber(discount), 0, sub);
  const tax = toNumber(taxAmount);
  
  const total = Math.max(0, sub - disc + tax);
  
  return roundToDecimals(total, decimals);
}

/**
 * Calculate all invoice totals in correct order
 * This is the main function that should be used for invoice calculations
 * 
 * @param {Object} params - Calculation parameters
 * @param {Array} params.items - Line items array
 * @param {number} params.discount - Discount amount
 * @param {number} params.taxRate - Tax rate percentage
 * @param {string} params.currencyCode - Currency code
 * @param {Object} params.gstConfig - GST configuration (optional)
 * @param {boolean} params.gstConfig.enabled - Whether GST is enabled
 * @param {boolean} params.gstConfig.isSameState - Whether same state transaction
 * @param {number} params.gstConfig.cgstRate - CGST rate
 * @param {number} params.gstConfig.sgstRate - SGST rate
 * @param {number} params.gstConfig.igstRate - IGST rate
 * @returns {Object} Calculated totals { subtotal, discountAmount, taxAmount, total, cgstAmount, sgstAmount, igstAmount }
 */
export function calculateInvoiceTotals({
  items = [],
  discount = 0,
  taxRate = 0,
  currencyCode = 'USD',
  gstConfig = null,
} = {}) {
  // Step 1: Calculate subtotal from items
  const subtotal = calculateSubtotal(items, currencyCode);
  
  // Step 2: Validate and clamp discount
  const discountAmount = clamp(toNumber(discount), 0, subtotal);
  
  // Step 3: Calculate discounted subtotal
  const discountedSubtotal = roundToDecimals(
    Math.max(0, subtotal - discountAmount),
    getDecimalPlaces(currencyCode)
  );
  
  let taxAmount = 0;
  let cgstAmount = 0;
  let sgstAmount = 0;
  let igstAmount = 0;
  
  // Step 4: Calculate tax
  if (gstConfig?.enabled) {
    // GST calculation
    if (gstConfig.isSameState) {
      // Same state: CGST + SGST
      cgstAmount = roundToDecimals(
        discountedSubtotal * (toNumber(gstConfig.cgstRate || 9) / 100),
        getDecimalPlaces(currencyCode)
      );
      sgstAmount = roundToDecimals(
        discountedSubtotal * (toNumber(gstConfig.sgstRate || 9) / 100),
        getDecimalPlaces(currencyCode)
      );
      taxAmount = cgstAmount + sgstAmount;
    } else {
      // Inter-state: IGST only
      igstAmount = roundToDecimals(
        discountedSubtotal * (toNumber(gstConfig.igstRate || 18) / 100),
        getDecimalPlaces(currencyCode)
      );
      taxAmount = igstAmount;
    }
  } else {
    // Standard tax calculation
    taxAmount = calculateTaxAmount(subtotal, discountAmount, taxRate, currencyCode);
  }
  
  // Step 5: Calculate final total
  const total = roundToDecimals(
    discountedSubtotal + taxAmount,
    getDecimalPlaces(currencyCode)
  );
  
  return {
    subtotal,
    discountAmount,
    discountedSubtotal,
    taxAmount,
    total,
    cgstAmount,
    sgstAmount,
    igstAmount,
  };
}

/**
 * Recalculate line items with updated amounts
 * @param {Array} items - Line items to recalculate
 * @param {string} currencyCode - Currency code for precision
 * @returns {Array} Line items with recalculated amounts
 */
export function recalculateLineItems(items = [], currencyCode = 'USD') {
  if (!Array.isArray(items)) {
    return [createEmptyLineItem()];
  }
  
  return items.map(item => createLineItem(item, currencyCode));
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Check if a line item is valid (has description and positive amount)
 * @param {Object} item - Line item to validate
 * @returns {boolean} Whether the item is valid
 */
export function isValidLineItem(item) {
  if (!item || typeof item !== 'object') {
    return false;
  }
  
  const hasDescription = item.description && item.description.trim().length > 0;
  const hasValidQuantity = toNumber(item.quantity) > 0;
  const hasValidRate = toNumber(item.rate) >= 0;
  
  return hasDescription && hasValidQuantity && hasValidRate;
}

/**
 * Check if an invoice has at least one valid line item
 * @param {Array} items - Line items array
 * @returns {boolean} Whether there's at least one valid item
 */
export function hasValidLineItems(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return false;
  }
  
  return items.some(isValidLineItem);
}

/**
 * Validate invoice totals for consistency
 * @param {Object} invoice - Invoice data object
 * @returns {Object} Validation result { isValid, errors }
 */
export function validateInvoiceTotals(invoice) {
  const errors = [];
  
  const calculatedTotals = calculateInvoiceTotals({
    items: invoice.items,
    discount: invoice.discount,
    taxRate: invoice.tax_rate,
    currencyCode: invoice.currency,
  });
  
  // Check if stored totals match calculated totals (within epsilon)
  const epsilon = 0.01;
  
  if (Math.abs(calculatedTotals.subtotal - toNumber(invoice.subtotal)) > epsilon) {
    errors.push('Subtotal mismatch');
  }
  
  if (Math.abs(calculatedTotals.taxAmount - toNumber(invoice.tax_amount)) > epsilon) {
    errors.push('Tax amount mismatch');
  }
  
  if (Math.abs(calculatedTotals.total - toNumber(invoice.total)) > epsilon) {
    errors.push('Total mismatch');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    calculatedTotals,
  };
}

const calculationsExports = {
  toNumber,
  roundToDecimals,
  getDecimalPlaces,
  clamp,
  calculateLineItemAmount,
  createLineItem,
  createEmptyLineItem,
  recalculateLineItems,
  calculateSubtotal,
  calculateTaxAmount,
  calculateTotal,
  calculateInvoiceTotals,
  isValidLineItem,
  hasValidLineItems,
  validateInvoiceTotals,
};

export default calculationsExports;
